import { Children, cloneElement, isValidElement, type ReactNode } from 'react';
import {
  type FieldErrors,
  FormProvider,
  type UseFormRegister,
  useForm,
} from 'react-hook-form';
import { type ZodEffects, type ZodObject, type ZodRawShape, z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';

import { FormCheckbox } from './controls/FormCheckbox';
import { FormCheckboxGroup } from './controls/FormCheckboxGroup';
import { FormCombobox } from './controls/FormCombobox';
import { FormControl } from './controls/FormControl';
import { FormDatePicker } from './controls/FormDatePicker';
import { FormIfField } from './controls/FormIfField';
import { FormInputFile } from './controls/FormInputFile';
import { FormInputPassword } from './controls/FormInputPassword';
import { FormInputText } from './controls/FormInputText';
import { FormRadio } from './controls/FormRadio';
import { FormRadioGroup } from './controls/FormRadioGroup';
import { FormSelect } from './controls/FormSelect';
import { FormTextarea } from './controls/FormTextarea';

export type FormProps = {
  defaultValues?: object;
  rules?: ZodObject<ZodRawShape> | ZodEffects<ZodObject<ZodRawShape>>;
  onSubmit: (formData: object) => void;
  children: React.ReactNode;
  className?: string;
  externalErrors?: FieldErrors;
};

export const Form = ({
  defaultValues,
  rules = z.object({}).refine(() => {}),
  onSubmit,
  children,
  className,
  externalErrors = {},
  ...props
}: FormProps) => {
  // const schema: ZodObject<ZodRawShape> =
  //   rules instanceof ZodEffects ? rules._def.schema : rules;

  // const form = useForm({
  //   shouldFocusError: true,
  //   ...(rules && Object.keys(schema?.shape || {}).length > 0
  //     ? { resolver: zodResolver(schema) }
  //     : {}),
  //   defaultValues,
  // });

  //needs declare all fields in zod even if no required
  const schema = rules;
  const form = useForm({
    shouldFocusError: true,
    resolver: zodResolver(schema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={className ?? undefined}
        {...props}
      >
        {renderChildrenRecursively(
          children,
          register,
          errors,
          externalErrors,
          schema,
        )}
      </form>
    </FormProvider>
  );
};

const controlList = [
  FormInputText,
  FormCheckboxGroup,
  FormCheckbox,
  FormCombobox,
  FormRadioGroup,
  FormRadio,
  FormTextarea,
  FormSelect,
  FormDatePicker,
  FormInputFile,
  FormInputPassword,
  FormIfField,
  FormControl,
];

const isRequired = (schema: ZodObject<ZodRawShape>, field: string) => {
  const shape = schema?.shape?.[field] || undefined;
  if (shape === undefined) {
    return false;
  }

  return !(shape.isOptional() || shape.isNullable());
};

const renderChildrenRecursively = (
  children: React.ReactNode,
  register: UseFormRegister<Record<string, string>>,
  errors: FieldErrors,
  externalErrors: FieldErrors,
  rules: ZodObject<ZodRawShape>,
): ReactNode => {
  return Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    const props = child.props as {
      name?: string;
      type?: string;
      children?: ReactNode;
    };

    if (controlList.some((control) => child.type === control)) {
      if (props.name) {
        const errorMessage =
          errors[props.name]?.message || externalErrors[props.name]?.message;
        const newProps = {
          required: isRequired(rules, props.name),
          error: errorMessage,
          ...props,
          ...register(props.name, {
            setValueAs: (value: unknown) => {
              return value === '' || value === null ? undefined : value;
            },
          }),
        };
        return cloneElement(child, newProps);
      }
    }

    if (props.children) {
      return cloneElement(
        child,
        props,
        renderChildrenRecursively(
          props.children,
          register,
          errors,
          externalErrors,
          rules,
        ),
      );
    }

    return child;
  });
};

Form.InputText = FormInputText;
Form.Checkbox = FormCheckbox;
Form.CheckboxGroup = FormCheckboxGroup;
Form.Combobox = FormCombobox;
Form.RadioGroup = FormRadioGroup;
Form.Radio = FormRadio;
Form.Textarea = FormTextarea;
Form.Select = FormSelect;
Form.DatePicker = FormDatePicker;
Form.InputFile = FormInputFile;
Form.InputPassword = FormInputPassword;
Form.IfField = FormIfField;
Form.Control = FormControl;
